import React, { useReducer } from 'react';
import api from '../../utils/api';
import SeasonContext from './seasonContext';
import seasonReducer from './seasonReducer';
import {
  GET_SEASONS,
  GET_CURRENT_SEASON,
  CREATE_SEASON,
  UPDATE_SEASON,
  DELETE_SEASON,
  SET_CURRENT_SEASON,
  GET_SEASON_STATS,
  SEASON_ERROR,
  CLEAR_SEASON_ERROR
} from '../types';

const SeasonState = props => {
  const initialState = {
    seasons: [],
    currentSeason: null,
    seasonStats: null,
    loading: true,
    error: null
  };

  const [state, dispatch] = useReducer(seasonReducer, initialState);

  // Get all seasons
  const getSeasons = async () => {
    try {
      const res = await api.get('/api/seasons');
      dispatch({
        type: GET_SEASONS,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: SEASON_ERROR,
        payload: err.response?.data?.msg || 'Error fetching seasons'
      });
    }
  };

  // Get current season
  const getCurrentSeason = async () => {
    try {
      const res = await api.get('/api/seasons/current');
      dispatch({
        type: GET_CURRENT_SEASON,
        payload: res.data
      });
    } catch (err) {
      if (err.response?.status !== 404) {
        dispatch({
          type: SEASON_ERROR,
          payload: err.response?.data?.msg || 'Error fetching current season'
        });
      }
    }
  };

  // Create new season
  const createSeason = async seasonData => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.post('/api/seasons', seasonData, config);
      dispatch({
        type: CREATE_SEASON,
        payload: res.data
      });
      
      // If this season was set as current, update currentSeason
      if (res.data.isCurrent) {
        dispatch({
          type: GET_CURRENT_SEASON,
          payload: res.data
        });
      }
      
      return res.data;
    } catch (err) {
      dispatch({
        type: SEASON_ERROR,
        payload: err.response?.data?.msg || 'Error creating season'
      });
      throw err;
    }
  };

  // Update season
  const updateSeason = async (id, seasonData) => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await api.put(`/api/seasons/${id}`, seasonData, config);
      dispatch({
        type: UPDATE_SEASON,
        payload: res.data
      });
      
      // If this season was set as current, update currentSeason
      if (res.data.isCurrent) {
        dispatch({
          type: GET_CURRENT_SEASON,
          payload: res.data
        });
      }
      
      return res.data;
    } catch (err) {
      dispatch({
        type: SEASON_ERROR,
        payload: err.response?.data?.msg || 'Error updating season'
      });
      throw err;
    }
  };

  // Set season as current
  const setCurrentSeason = async id => {
    try {
      const res = await api.put(`/api/seasons/${id}/set-current`);
      dispatch({
        type: SET_CURRENT_SEASON,
        payload: res.data.season
      });
      return res.data.season;
    } catch (err) {
      dispatch({
        type: SEASON_ERROR,
        payload: err.response?.data?.msg || 'Error setting current season'
      });
      throw err;
    }
  };

  // Delete season
  const deleteSeason = async id => {
    try {
      await api.delete(`/api/seasons/${id}`);
      dispatch({
        type: DELETE_SEASON,
        payload: id
      });
    } catch (err) {
      dispatch({
        type: SEASON_ERROR,
        payload: err.response?.data?.msg || 'Error deleting season'
      });
      throw err;
    }
  };

  // Get season statistics
  const getSeasonStats = async seasonId => {
    try {
      const res = await api.get(`/api/seasons/stats/${seasonId}`);
      dispatch({
        type: GET_SEASON_STATS,
        payload: res.data
      });
      return res.data;
    } catch (err) {
      dispatch({
        type: SEASON_ERROR,
        payload: err.response?.data?.msg || 'Error fetching season statistics'
      });
    }
  };

  // Clear errors
  const clearSeasonError = () => dispatch({ type: CLEAR_SEASON_ERROR });

  return (
    <SeasonContext.Provider
      value={{
        seasons: state.seasons,
        currentSeason: state.currentSeason,
        seasonStats: state.seasonStats,
        loading: state.loading,
        error: state.error,
        getSeasons,
        getCurrentSeason,
        createSeason,
        updateSeason,
        setCurrentSeason,
        deleteSeason,
        getSeasonStats,
        clearSeasonError
      }}
    >
      {props.children}
    </SeasonContext.Provider>
  );
};

export default SeasonState;